// Small inline icons (stroke, 24px grid) so the app needs no icon font or extra requests.
import type { ReactNode } from 'react';

function Svg({ children, size = 20, label }: { children: ReactNode; size?: number; label?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      aria-label={label}
    >
      {children}
    </svg>
  );
}

const handset =
  'M5 4h3.2l1.6 4-2 1.3a11 11 0 0 0 6.9 6.9l1.3-2 4 1.6V19a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z';

export const PhoneIcon = (p: { size?: number }) => (
  <Svg size={p.size}>
    <path d={handset} />
  </Svg>
);

export const HangUpIcon = (p: { size?: number }) => (
  <Svg size={p.size}>
    <g transform="rotate(135 12 12)">
      <path d={handset} />
    </g>
  </Svg>
);

export const MicIcon = () => (
  <Svg>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
  </Svg>
);

export const MicOffIcon = () => (
  <Svg>
    <path d="M15 10V6a3 3 0 0 0-5.7-1.3M9 9v2a3 3 0 0 0 4.6 2.5M5 11a7 7 0 0 0 11 5.7M19 11a7 7 0 0 1-.6 2.8M12 18v3M3 3l18 18" />
  </Svg>
);

export const PauseIcon = () => (
  <Svg>
    <path d="M9 5v14M15 5v14" />
  </Svg>
);

export const PlayIcon = () => (
  <Svg>
    <path d="M7 5l12 7-12 7z" />
  </Svg>
);

export const TransferIcon = () => (
  <Svg>
    <path d="M4 8h13M13 4l4 4-4 4M20 16H7M11 12l-4 4 4 4" />
  </Svg>
);

export const KeypadIcon = () => (
  <Svg>
    {[5, 11, 17].flatMap((y) => [6, 12, 18].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.3" />))}
    <circle cx="12" cy="22" r="1.3" />
  </Svg>
);

export const BackspaceIcon = () => (
  <Svg>
    <path d="M9 5h11v14H9l-6-7zM13 9.5l5 5M18 9.5l-5 5" />
  </Svg>
);

export const ArrowOutIcon = () => (
  <Svg size={16}>
    <path d="M7 17L17 7M9 7h8v8" />
  </Svg>
);

export const ArrowInIcon = () => (
  <Svg size={16}>
    <path d="M17 7L7 17M15 17H7V9" />
  </Svg>
);

export const MissedIcon = () => (
  <Svg size={16}>
    <path d="M4 7l6 6 4-4 6 6M20 10v5h-5" />
  </Svg>
);

export const BellIcon = () => (
  <Svg size={18}>
    <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15zM10 20a2 2 0 0 0 4 0" />
  </Svg>
);

export const SettingsIcon = () => (
  <Svg size={18}>
    <path d="M4 7h10M18 7h2M4 17h2M10 17h10" />
    <circle cx="16" cy="7" r="2" />
    <circle cx="8" cy="17" r="2" />
  </Svg>
);

export function Logo() {
  return (
    <div className="logo">
      <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden>
        <rect x="1" y="1" width="26" height="26" rx="8" fill="var(--brand)" />
        <path d="M9 15.5a6 6 0 0 1 10 0M11.5 18.5a2.6 2.6 0 0 1 5 0" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" />
        <circle cx="14" cy="9.5" r="1.6" fill="#fff" />
      </svg>
      <span>CloudPhone</span>
    </div>
  );
}

/** Small filled play / pause for voicemail buttons. */
export const PlaySmallIcon = () => (
  <Svg size={14}>
    <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" stroke="none" />
  </Svg>
);

export const PauseSmallIcon = () => (
  <Svg size={14}>
    <path d="M8 5h3v14H8zM13 5h3v14h-3z" fill="currentColor" stroke="none" />
  </Svg>
);

export const VoicemailIcon = (p: { size?: number }) => (
  <Svg size={p.size}>
    <circle cx="6.5" cy="12" r="3.5" />
    <circle cx="17.5" cy="12" r="3.5" />
    <path d="M6.5 15.5h11" />
  </Svg>
);

export const AddPersonIcon = () => (
  <Svg>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 20a6.5 6.5 0 0 1 13 0M19 8v6M16 11h6" />
  </Svg>
);

export const PeopleIcon = (p: { size?: number }) => (
  <Svg size={p.size}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3 19.5a6 6 0 0 1 12 0" />
    <circle cx="17" cy="9" r="2.6" />
    <path d="M15.5 14.2A5 5 0 0 1 21.5 19" />
  </Svg>
);

export const SwitchIcon = () => (
  <Svg>
    <path d="M7 4v14M3.5 14.5 7 18l3.5-3.5M17 20V6M13.5 9.5 17 6l3.5 3.5" />
  </Svg>
);

export const CloseIcon = (p: { size?: number }) => (
  <Svg size={p.size}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Svg>
);

export const EyeIcon = (p: { size?: number }) => (
  <Svg size={p.size}>
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </Svg>
);

export const EyeOffIcon = (p: { size?: number }) => (
  <Svg size={p.size}>
    <path d="M10.6 5.1A10.5 10.5 0 0 1 12 5c6.4 0 10 7 10 7a17.6 17.6 0 0 1-2.9 3.9" />
    <path d="M6.6 6.6C3.7 8.4 2 12 2 12s3.6 7 10 7a9.7 9.7 0 0 0 5.4-1.6" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    <path d="M3 3l18 18" />
  </Svg>
);
